import React, { createContext, useContext, useState, useEffect } from 'react';
import { getMe } from '../api/auth';
import client from '../api/client';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem('roar_token');
      if (token) {
        try {
          // If a token exists, try to fetch the current user
          const data = await getMe();
          if (data && data.id) {
            setUser(data);
            setIsLoggedIn(true);
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
          // If the token is invalid or expired, client.js will handle clearing it via interceptor
        }
      }
      setIsLoading(false);
    };

    fetchUser();

    // Listen for custom 'logout' event (emitted by client.js on 401, or by components)
    const handleLogoutEvent = () => {
      setIsLoggedIn(false);
      setUser(null);
    };

    window.addEventListener('logout', handleLogoutEvent);

    return () => {
      window.removeEventListener('logout', handleLogoutEvent);
    };
  }, []);

  const login = (userData, token) => {
    setIsLoggedIn(true);
    setUser(userData);
    localStorage.setItem('roar_token', token);
  };

  const logout = async () => {
    try {
      // Import logout from api/auth if needed, or call it here
      const { logout: apiLogout } = require('../api/auth');
      await apiLogout();
    } catch (e) {
      console.error(e);
    } finally {
      localStorage.removeItem('roar_token');
      window.dispatchEvent(new Event('logout'));
      
      // We don't want to force refresh necessarily, React state should update
      if (window.location.pathname !== '/') {
          window.location.href = '/';
      }
    }
  };

  const value = {
    isLoggedIn,
    user,
    isLoading,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
